import { ModalController } from "./modal";
import { InlineController } from "./inline";
import { AssistedController } from "./assisted";
import { handleDigilockerCallback } from "./modal/digilocker-callback";
import type {
  ModalOptions,
  InlineOptions,
  DigiLockerCallbackOptions,
  OtpGateOptions,
  OtpVerifyResult,
} from "./types";
import type { AssistedOptions } from "./assisted/types";
import { mountPrivacyCenter } from "./privacy-center";
import type { PrivacyCenterHandle, PrivacyCenterOptions } from "./privacy-center/types";

let activeModal: ModalController | null = null;
let activeInline: InlineController | null = null;
let activeAssisted: AssistedController | null = null;
const activePrivacyCenters = new Set<PrivacyCenterHandle>();

/**
 * Initialize a modal consent dialog.
 * Renders a full-screen overlay with the consent form.
 */
function init(options: ModalOptions): void {
  // Destroy any existing instance
  destroy();

  activeModal = new ModalController(options);
  activeModal.init().catch((err) => {
    options.onError?.(err);
  });
}

/**
 * Initialize an inline consent form.
 * Renders consent purposes inside a target container element.
 */
function inline(options: InlineOptions): void {
  // Destroy any existing inline instance
  if (activeInline) {
    activeInline.destroy();
    activeInline = null;
  }

  activeInline = new InlineController(options);
  activeInline.init().catch((err) => {
    options.onError?.(err);
  });
}

function updateToken(accessToken: string): void {
  activeModal?.updateToken(accessToken);
  activeInline?.updateToken(accessToken);
}

function assisted(options: AssistedOptions): void {
  if (activeAssisted) {
    activeAssisted.destroy();
    activeAssisted = null;
  }

  activeAssisted = new AssistedController(options);
  activeAssisted.init().catch((err) => {
    options.onError?.(err);
  });
}

/**
 * Mount the hosted Privacy Center in an iframe inside a target element.
 * Returns a handle whose `destroy()` removes it.
 */
function privacyCenter(options: PrivacyCenterOptions): PrivacyCenterHandle {
  const handle = mountPrivacyCenter(options);
  const tracked: PrivacyCenterHandle = {
    iframe: handle.iframe,
    destroy: () => {
      activePrivacyCenters.delete(tracked);
      handle.destroy();
    },
  };
  activePrivacyCenters.add(tracked);
  return tracked;
}

/**
 * Destroy all active consent instances.
 */
function destroy(): void {
  if (activeModal) {
    activeModal.destroy();
    activeModal = null;
  }
  if (activeInline) {
    activeInline.destroy();
    activeInline = null;
  }
  if (activeAssisted) {
    activeAssisted.destroy();
    activeAssisted = null;
  }
  for (const handle of [...activePrivacyCenters]) {
    handle.destroy();
  }
}

/**
 * Handle DigiLocker callback (for guardian verification redirect flow).
 * Call this on the DigiLocker callback page.
 */
function digilockerCallback(options: DigiLockerCallbackOptions): void {
  handleDigilockerCallback(options);
}

const RedactoConsent = {
  init,
  inline,
  assisted,
  privacyCenter,
  destroy,
  digilockerCallback,
  updateToken,
  version: "0.1.0",
};

export default RedactoConsent;
export { init, inline, assisted, privacyCenter, destroy, digilockerCallback, updateToken };
export type {
  ModalOptions,
  InlineOptions,
  DigiLockerCallbackOptions,
  OtpGateOptions,
  OtpVerifyResult,
  AssistedOptions,
  PrivacyCenterOptions,
  PrivacyCenterHandle,
};
export type { Settings } from "./api/types";
export { ApiError } from "./shared/api-errors";
export type { ApiErrorCode } from "./shared/types";
